import React, { Component } from 'react';
import Icon from '../../../../../icons/Icon';
import { CaseChartPanel, CaseFieldPanel, CasePanel, CasePanelType } from '../../../../../domain/case/CaseDomains';
import DomainConfigurationPanelCardComponent from './DomainConfigurationPanelCardComponent';
import { TransactionJsonModel } from '../../../../../domain/stream/transaction/TransactionDomains';
import RoleHelper from '../../../../../RoleHelper';
import { RootState } from '../../../../../redux/store';
import { ConfigurationState } from '../../../../../redux/reducers/ConfigurationReducer';
import { changeConfiguration } from '../../../../../redux/actions/ConfigurationActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import DomainConfigurationChartPanelComponent from './DomainConfigurationChartPanelComponent';
import DomainConfigurationCardContainer from './DomainConfigurationCardContainer';
import DataType from '../../../../../constant/DataType';
import ComponentName from '../../../../../constant/ComponentName';
import LanguageUtil from '../../../../../util/LanguageUtil';

class DomainConfigurationPanelComponent extends Component<Props, State> {
  public state: State = new State();

  public render() {
    const { transaction } = this.state;
    const {
      configuration: { configuration },
      enrichmentNames,
    } = this.props;
    const disabled = !RoleHelper.canEdit('CaseConfiguration');
    return (
      <div className='h-full w-full max-h-[calc(100vh-16rem)] grid grid-cols-2 gap-6 scrollable-editor-container overflow-scroll px-5 py-5'>
        {configuration?.panels?.map((panel, index) => {
          switch (panel.type) {
            case CasePanelType.CHART:
              return (
                <DomainConfigurationCardContainer
                  panel={panel}
                  disabled={disabled}
                  changePanel={this.onChangePanel}
                  key={index}
                  deletePanel={this.deletePanel}
                  type={panel.type}
                  content={
                    <DomainConfigurationChartPanelComponent
                      key={index + 'chart'}
                      panel={panel as CaseChartPanel}
                      changePanel={this.onChangePanel}
                      deletePanel={this.deletePanel}
                      disabled={disabled}
                      index={index}
                    />
                  }
                  index={index}
                />
              );
            default:
              return (
                <DomainConfigurationCardContainer
                  panel={panel}
                  changePanel={this.onChangePanel}
                  deletePanel={this.deletePanel}
                  key={index}
                  disabled={disabled}
                  type={panel.type}
                  content={
                    <DomainConfigurationPanelCardComponent
                      panel={panel as CaseFieldPanel}
                      enrichmentNames={enrichmentNames}
                      changePanel={this.onChangePanel}
                      domainConfiguration={configuration}
                      transaction={transaction}
                      disabled={disabled}
                    />
                  }
                  index={index}
                />
              );
          }
        })}
        <div
          className={`max-h-[260px] h-[260px] flex gap-x-5 bg-[#FFFFFF] justify-center items-center border-[#65B0F2] w-full border-[2px] border-dashed rounded-[10px]`}
        >
          <div
            className={`flex flex-col gap-y-3 w-full h-full items-center justify-center ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            onClick={() => this.addPanel(CasePanelType.PANEL)}
            data-testid={`${ComponentName.CONFIGURATION}_panel_add_panel_button`}
          >
            <Icon icon={'create'} size={32} color={!disabled ? ' #65B0F2' : 'E0E4E8'} />
            <span className={`${!disabled ? 'text-[#65B0F2]' : 'text-[E0E4E8]'} text-lg`}>
              {LanguageUtil.get({ id: 'common.label.addNewPanel', defaultMessage: 'Add a new panel' })}
            </span>
          </div>
          <div
            className={`flex flex-col gap-y-3  w-full h-full items-center justify-center ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            onClick={() => this.addPanel(CasePanelType.CHART)}
            data-testid={`${ComponentName.CONFIGURATION}_panel_add_chart_button`}
          >
            <Icon icon={'bar-vertical-chart'} size={32} color={!disabled ? ' #65B0F2' : 'E0E4E8'} />
            <span className={`${!disabled ? 'text-[#65B0F2]' : 'text-[E0E4E8]'} text-lg`}>
              {LanguageUtil.get({ id: 'common.label.addNewChart', defaultMessage: 'Add a new chart' })}
            </span>
          </div>
        </div>
      </div>
    );
  }

  public componentDidMount() {
    const {
      configuration: { eventItems, configuration },
    } = this.props;
    this.setState({
      transaction: eventItems.find((e) => e.name == configuration.dataSource),
    });
  }

  private onChangePanel = () => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    changeConfiguration(configuration);
  };

  private addPanel = (type: CasePanelType) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    if (!RoleHelper.canEdit('CaseConfiguration')) return;
    configuration.panels.push(this.getEmptyPanelByType(type));
    changeConfiguration(configuration);
  };

  private getEmptyPanelByType = (type: CasePanelType) => {
    switch (type) {
      case CasePanelType.PANEL:
        return this.getEmptyFieldPanel();
      case CasePanelType.CHART:
        return this.getEmptyChartPanel();
    }
  };

  private getEmptyFieldPanel = (): CaseFieldPanel => {
    return {
      type: CasePanelType.PANEL,
      title: '',
      enabled: true,
      gridType: 'odd',
      fields: [],
    };
  };

  private getEmptyChartPanel = (): CaseChartPanel => {
    return {
      type: CasePanelType.CHART,
      title: '',
      enabled: true,
      gridType: 'odd',
      selectedChart: '',
      selectedDashboard: '',
      filterWith: {
        value: '',
        dataType: DataType.STRING,
      },
      chartFilterField: {
        value: '',
        dataType: DataType.STRING,
      },
    };
  };

  private deletePanel = (panel: CasePanel) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    const { panels } = configuration;
    if (!RoleHelper.canEdit('CaseConfiguration')) return;
    const panelIndex = panels.indexOf(panel as CaseFieldPanel);
    if (panelIndex != -1) {
      configuration.panels.splice(panelIndex, 1);
    }
    changeConfiguration(configuration);
  };
}

interface Props extends ReduxProps {
  enrichmentNames: string[];
}

const mapStateToProps = (state: RootState) => ({
  lastCategory: state.page.lastCategory,
  page: state.page,
  filter: state.page.filter,
  configuration: state.configuration as ConfigurationState,
});

const mapDispatchToProps = {
  changeConfiguration,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

class State {
  transaction: TransactionJsonModel | undefined;
}

export default connector(DomainConfigurationPanelComponent);
